import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { UpdateFromPipelineContext } from "./UpdateFromPipelineContext";
import actions from "../actions";
import { UpdateTransactionModal } from "../../TransactionDetailsPage/Main/Details/UpdateTransactionModal";
import { tryCatchHandlr } from "../../shared/Utilities";
import { buildCompleteClientRoleList } from "../../reducers/tdpPeopleReducer";

const basePath = "/api/v4/transactions/details";
const listingPath = (listingUuid) => `${basePath}/${listingUuid}`;
const csrfToken = () => window.ReactOnRails.authenticityToken();

const TDIUpdateTransactionModal = ({ sources, propertyTypes }) => {
  const { state, dispatch } = useContext(UpdateFromPipelineContext);

  const listing = state?.updateTransactionModal?.listing;
  const listingUuid = listing?.uuid;
  const isModalOpen = state?.updateTransactionModal?.isUpdateTransactionModalOpen;
  const updateTransactionMlsData = state?.updateTransactionModal?.mlsListingData;
  const preStatus = listing?.status;
  const transactionType = listing?.type;

  const [clients, setClients] = useState(null);
  const [clientRoles, setClientRoles] = useState(null);
  const [connectedService, setConnectedService] = useState(null);

  useEffect(() => {
    if (!isModalOpen || !listingUuid || (transactionType !== "buyer" && transactionType !== "tenant")) return;

    let cancelled = false;
    const fetchPeopleData = async () => {
      try {
        const response = await axios.get(`/api/v4/transactions/${listingUuid}/people/`, {
          params: { authenticity_token: csrfToken() },
        });
        if (cancelled) return;
        // eslint-disable-next-line prefer-destructuring
        const data = response.data;
        setClients(data.listing_clients || []);
        setClientRoles(buildCompleteClientRoleList(data));
      } catch {
        if (!cancelled) {
          setClients([]);
          setClientRoles([]);
        }
      }
    };
    fetchPeopleData();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, listingUuid, transactionType]);

  useEffect(() => {
    if (!isModalOpen) {
      setClients(null);
      setClientRoles(null);
      setConnectedService(null);
    }
  }, [isModalOpen]);

  const propertyDetails = useMemo(() => {
    if (!listing) return null;
    const details = listing.propertyDetails || {};
    return {
      id: listing.address?.id,
      street_address: listing.address?.streetAddress || "",
      city: listing.address?.city || "",
      state: listing.address?.locality || "",
      zip: listing.address?.postalCode || "",
      country: "US",
      neighborhood: listing.address?.neighborhood || "",
      county: listing.address?.county || "",
      beds: details.beds || "",
      baths: details.baths || "",
      sqft: details.sqft || "",
      year_built: details.year_built || "",
      parking: details.parking || "",
      foundation: details.foundation || "",
      property_type: listing.propertyType || { id: null, name: null },
    };
  }, [listing]);

  const listingDetailsData = useMemo(() => {
    if (!listing) return null;
    return {
      mls_number: listing.mlsNumber || "",
      price: listing.listingPrice || "",
      description: listing.description || "",
      source: listing.sourceId ? { id: listing.sourceId } : null,
    };
  }, [listing]);

  const transactionDetails = useMemo(() => {
    if (!listing) return null;
    return {
      title: listing.title || "",
      status: listing.status || "pending",
      use_custom_title: listing.useCustomTitle ?? false,
      est_close_price: listing.closePrice || "",
    };
  }, [listing]);

  const accountSources = useMemo(() => {
    if (!sources) return [];
    return sources.map((s) => ({
      label: s.name || s.label,
      value: s.id || s.value,
    }));
  }, [sources]);

  const accountPropertyTypes = useMemo(() => {
    if (!propertyTypes) return [];
    return propertyTypes.map((pt) => ({
      label: pt.name || pt.label,
      value: pt.id || pt.value,
    }));
  }, [propertyTypes]);

  const mutualAcceptanceDateMilestone = useMemo(
    () => ({
      id: "mutual_acceptance",
      name: "Mutual Acceptance Date",
      date: listing?.mutualAcceptanceDate || "",
    }),
    [listing],
  );

  const closeDateMilestone = useMemo(
    () => ({
      id: "close_date",
      name: "Close Date",
      date: listing?.closeDate || "",
    }),
    [listing],
  );

  const putDetailAsThunk = async ({ uuid, payload }) => {
    const req = axios.put(listingPath(uuid), {
      authenticity_token: csrfToken(),
      listing: payload,
    });

    const [, err] = await tryCatchHandlr(req);

    if (err) {
      throw err;
    }

    if (payload.address_attributes) {
      dispatch(
        actions.listings.updateListing(listing, listing.status, {
          street_address: payload.address_attributes.street_address,
          city: payload.address_attributes.city,
          locality: payload.address_attributes.locality,
          postal_code: payload.address_attributes.postal_code,
        }),
      );
    }
  };

  const updateDateAsThunk = async () => {
    // no-op: dates are consolidated into the main putDetailAsThunk payload
  };

  const closeUpdateTransactionModal = () => {
    dispatch(actions.updateTransactionModal.toggleUpdateTransactionModal(false, null, null));
  };

  const clearUpdateTransactionMlsData = () => {
    dispatch(actions.updateTransactionModal.clearMlsData());
  };

  const closeModalAsThunk = () => {
    dispatch(actions.listings.fetchStart());
  };

  const revertStatus = (_uuid, status) => {
    dispatch(actions.listings.updateListing(listing, status));
    dispatch(actions.listings.setStatusLoading(listing.uuid, false));
  };

  const stopStatusLoading = () => {
    dispatch(actions.listings.setStatusLoading(listing.uuid, false));
  };

  const noop = () => {};

  return (
    <UpdateTransactionModal
      isUpdateTransactionModalOpen={isModalOpen}
      updateTransactionMlsData={updateTransactionMlsData}
      propertyDetails={propertyDetails}
      listingDetailsData={listingDetailsData}
      transactionDetails={transactionDetails}
      accountSources={accountSources}
      accountPropertyTypes={accountPropertyTypes}
      mutualAcceptanceDateMilestone={mutualAcceptanceDateMilestone}
      closeDateMilestone={closeDateMilestone}
      uuid={listingUuid}
      preStatus={preStatus}
      putDetailAsThunk={putDetailAsThunk}
      updateDateAsThunk={updateDateAsThunk}
      closeModalAsThunk={closeModalAsThunk}
      closeUpdateTransactionModal={closeUpdateTransactionModal}
      clearUpdateTransactionMlsData={clearUpdateTransactionMlsData}
      setClosedPrice={noop}
      revertStatus={revertStatus}
      stopStatusLoading={stopStatusLoading}
      clients={clients}
      transactionType={transactionType}
      connectedService={connectedService}
      setConnectedService={setConnectedService}
      clientRoles={clientRoles}
      consolidateDates
    />
  );
};

TDIUpdateTransactionModal.propTypes = {
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
  propertyTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
};

TDIUpdateTransactionModal.defaultProps = {
  sources: [],
  propertyTypes: [],
};

export default TDIUpdateTransactionModal;
